<template>
  <div class="input-group" :class="{ 'input-group-sm': size === 'sm' }">
    <span v-if="unitPosition === 'prefix'" class="input-group-text">{{
      unit
    }}</span>
    <input
      :id="inputId"
      v-model="text"
      type="number"
      class="form-control text-end"
      :class="{ 'is-invalid': invalid || outOfBounds }"
      :aria-invalid="invalid || outOfBounds ? 'true' : undefined"
      :min="min"
      :max="max ?? undefined"
      :step="step"
      :disabled="disabled"
      :placeholder="placeholder"
      :aria-label="ariaLabel ?? undefined"
      @blur="normalise"
    />
    <span v-if="unitPosition === 'suffix'" class="input-group-text">{{
      unit
    }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, useId } from "vue";

/**
 * A numeric input with a unit affix — MWh, %, €/MWh.
 *
 * The model is a number or null (empty), never the raw string, so a cleared field reads as "not
 * set" rather than as zero: the pool editor has to tell "0 MWh listed" from "nothing entered yet".
 */
const props = withDefaults(
  defineProps<{
    unit?: string;
    unitPosition?: "prefix" | "suffix";
    min?: number;
    max?: number | null;
    step?: number | string;
    disabled?: boolean;
    invalid?: boolean;
    placeholder?: string;
    /** Set when a `<label for>` outside the component owns the field; generated otherwise. */
    id?: string;
    ariaLabel?: string | null;
    size?: "sm" | "md";
  }>(),
  {
    unit: "MWh",
    unitPosition: "suffix",
    min: 0,
    max: null,
    step: 1,
    disabled: false,
    invalid: false,
    placeholder: "",
    id: undefined,
    ariaLabel: null,
    size: "md",
  }
);

const model = defineModel<number | null>({ default: null });

const generatedId = useId();
const inputId = computed(() => props.id ?? generatedId);

/**
 * Snap a typed value onto the field's `step`, so a whole-MWh field never carries a fraction: the
 * take endpoints take `dict[int, int]` and answer a `10.5` with an opaque 422 (C-FIX1-02).
 */
const toStep = (value: number) => {
  const step = Number(props.step);
  if (!Number.isFinite(step) || step <= 0) return value;
  const decimals = String(step).split(".")[1]?.length ?? 0;
  return Number((Math.round(value / step) * step).toFixed(decimals));
};

// `v-model` on `<input type="number">` hands back a number when the text parses and the raw string
// when it does not (Vue's `looseToNumber`), so the setter has to accept both.
const text = computed<string | number>({
  get: () =>
    model.value === null || model.value === undefined ? "" : model.value,
  set: (value) => {
    const parsed = typeof value === "number" ? value : Number(value.trim());
    if (value === "" || Number.isNaN(parsed)) {
      model.value = null;
      return;
    }
    model.value = parsed;
  },
});

// A number input keeps its own text while focused, so snapping mid-typing fights the user. Snap
// onto the step once, on blur. Bounds are never enforced here: a value outside them stays as typed
// and the field turns invalid, so the caller can say what the limit is and refuse the submit —
// silently clamping made a typed take vanish under a 0 MWh ceiling (2026-09-16).
const normalise = () => {
  if (model.value === null) return;
  model.value = toStep(model.value);
};

const outOfBounds = computed(
  () =>
    model.value !== null &&
    (model.value < props.min || (props.max !== null && model.value > props.max))
);
</script>
